import Vue from 'vue';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

// Apollo GraphQL configuration
import { ApolloClient, createNetworkInterface } from 'apollo-client';
import VueApollo from 'vue-apollo';
import { Capacitor } from '@capacitor/core';

import { version } from '../../../package.json';
import store from '../store';
import MFAPhoneAuth from '../plugins/mfa-phone-auth';

let host = process.env.VUE_APP_API_HOST;
const platform = Capacitor.getPlatform(); // returns 'ios', 'android', or 'web'

// Refresh threshold: refresh token if it expires within this many seconds
const TOKEN_REFRESH_THRESHOLD_SECONDS = 300; // 5 minutes

/**
 * Parse JWT and check if it's expiring soon
 * @param {string} token - JWT token
 * @returns {boolean} - true if token expires within threshold
 */
function isTokenExpiringSoon(token) {
  if (!token) return true;

  try {
    // JWT format: header.payload.signature
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;

    // Return true if token expires within threshold
    return timeUntilExpiry < TOKEN_REFRESH_THRESHOLD_SECONDS * 1000;
  } catch (e) {
    // If we can't parse the token, consider it expiring
    return true;
  }
}

/**
 * Refresh the native token if needed
 * @returns {string|null} - New token or null if refresh failed
 */
async function refreshNativeTokenIfNeeded() {
  const currentToken = store.state.user.token;

  if (!isTokenExpiringSoon(currentToken)) {
    return currentToken;
  }

  try {
    const result = await MFAPhoneAuth.refreshToken();
    if (result.idToken) {
      // Update the store with the new token
      store.commit('user/updateToken', { token: result.idToken });
      return result.idToken;
    }
  } catch (error) {
    // Token refresh failed - return current token and let API handle auth error
  }

  return currentToken;
}

const networkInterface = createNetworkInterface({
  uri: `${host}/api`,
});

networkInterface.use([
  {
    applyMiddleware: async (req, next) => {
      req.options.headers = {
        ...req.options.headers,
        'x-app-version': `${version}`,
        'x-app-platform': `${platform}`,
      };

      // Get auth token
      let token = null;
      const user = firebase.auth().currentUser;

      if (user) {
        // Web flow: Firebase SDK handles token refresh automatically
        token = await user.getIdToken();
      } else if (Capacitor.isNativePlatform() && store.state.user.token) {
        // Native flow: check if token needs refresh
        token = await refreshNativeTokenIfNeeded();
      }

      if (token) {
        req.options.headers.authorization = `${token}`;
      }

      next();
    },
  },
]);

const apolloClient = new ApolloClient({
  networkInterface,
  // connectToDevTools: process.env.NODE_ENV === 'development',
});

Vue.use(VueApollo);

const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
  defaultOptions: {
    $loadingKey: 'loading',
  },
});

export default apolloProvider;
